import { useApolloClient } from '@apollo/client';

import { cache } from '@/container/apollo-client/cache';
import {
  CURSOR_MEMBER_MESSAGES,
  CURSOR_PINNED_MEMBER_MESSAGES,
  CURSOR_RECENT_CHAT_MESSAGES,
} from '@/graphql/cursor.graphql';
import { QUERY_LIST_SCHEDULED_CHAT_MESSAGES, QUERY_MEMBER_PIN_MESSAGE_COUNT } from '@/graphql/query.graphql';
import {
  ChatMessageSubscription,
  GetChatMessagesV2Query,
  MessageTypeEnum,
  useChatMessageSubscription,
  useworkspaceAuthMeV2Query,
} from '@/types/generated/graphql';
import { cacheOnly } from '@shared/constants';
import {
  cacheMarkRepliedToDeleted,
  cachePrependRoomAttachments,
  cacheRefetchRoomAttachments,
} from '@/graphql/cache/cache-mutations';
import { useAcknowledgeDelivery } from '@modules/conversations/features/member-conversation/hooks/use-acknowledge-delivery';
import { parseAiCallSummaryCard } from '@modules/conversations/features/ai-call-summary/ai-call-summary.utils';
import { useAiTakeoverStore } from '@modules/phone/store/ai-takeover.store';

/**
 * Ends this member's pending takeover from the AI call summary card: `taken_over` means the AI has left
 * (the live call event says the same; this covers whichever comes first), and `in_progress` means the
 * claim was released because the ring timed out or the member hung up before the swap.
 */
function endAiTakeoverWhenTakenOver(data: ChatMessageSubscription) {
  const { takeover, resetTakeover } = useAiTakeoverStore.getState();
  if (!takeover || data.chatMessage?.type !== MessageTypeEnum.ai_call_summary) return;

  const card = parseAiCallSummaryCard(data.chatMessage.metadata);
  if (card?.messageConversationId !== takeover.messageConversationId) return;
  if (card.status === 'taken_over' || card.status === 'in_progress') resetTakeover();
}

export function useMemberConversationSubscription() {
  const acknowledgeDelivery = useAcknowledgeDelivery();
  const client = useApolloClient();
  const { data: workspaceAuthMe } = useworkspaceAuthMeV2Query(cacheOnly);
  const selfWorkspaceMemberId = workspaceAuthMe?.workspaceAuthMeV2?.data?.currentUserWorkspace?.id;

  useChatMessageSubscription({
    onData: (res) => handleOnData(res?.data?.data as ChatMessageSubscription),
  });

  function handleOnData(data: ChatMessageSubscription) {
    // The message has reached this client, which is what delivered means — and this handler runs
    // for every room, so it is true whichever chat the member is looking at. Done before the cache
    // work below, which returns early for a message this client already appended.
    acknowledgeDelivery({
      roomId: data.chatMessage?.room?.id,
      chatMessageId: data.chatMessage?.id,
      senderId: data.chatMessage?.sender?.id,
    });

    endAiTakeoverWhenTakenOver(data);

    // The members sidebar and its "view all" are ordered by last message, so any message reorders
    // them — one you received, and one you sent, which arrives here too. Refetched rather than
    // patched: the ordering, the unread count and the preview are all the server's answer, and
    // rebuilding them here would be a second implementation of them that could disagree.
    // Included as the document rather than by operation name: a name that does not match any active
    // query is a silent no-op, which is exactly how the thread refetch failed earlier in this work.
    client.refetchQueries({ include: [CURSOR_RECENT_CHAT_MESSAGES] });

    // A delete is republished to every member, the deleter included, after the server has dropped the
    // message's files (own or forwarded) from the room list. The payload withholds chatAttachments on
    // a deleted message, so the list is asked again rather than patched.
    if (data.chatMessage?.isDeleted) {
      cacheRefetchRoomAttachments(data.chatMessage.room?.id);
    }

    const cachedMessages = cache.readQuery<GetChatMessagesV2Query>({
      query: CURSOR_MEMBER_MESSAGES,
      variables: {
        roomId: data.chatMessage?.room?.id,
      },
    });
    // need to check this condition because in case of primary sender among groups, it would be already appended
    const alreadyAppendedToCache = cachedMessages?.getChatMessagesV2?.data?.some(
      (message) =>
        message?.id === data?.chatMessage?.id ||
        (Number(message?.id) < 0 &&
          message?.body === data.chatMessage?.body &&
          message?.sender?.id === data.chatMessage?.sender?.id)
    );

    if (alreadyAppendedToCache) {
      if (data.chatMessage?.isDeleted && data.chatMessage.id) {
        cacheMarkRepliedToDeleted(data.chatMessage.id);
      }

      // A republish of a message this client already holds means something on it changed rather
      // than a new one arriving — today that is a pin, an unpin, a delete, or an AI call summary card
      // republished with its late recording in `metadata`. Apollo has already merged the payload into
      // the normalised message, so the bubble's own state (and the card) is current; what
      // it cannot do is change list membership or a count. The pinned bar and its badge read those,
      // so they are asked again here rather than going stale until the thread is reopened.
      client.refetchQueries({ include: [QUERY_MEMBER_PIN_MESSAGE_COUNT, CURSOR_PINNED_MEMBER_MESSAGES] });

      return;
    }

    // Your own message that this composer did not send — no surrogate matched it above — was sent for
    // you by the scheduler (or from another tab), so the scheduled list is asked again. The server
    // claims chatMessageId on the schedule before publishing, and useScheduleChatMessage hides a row
    // that has one, so a refetch racing the row's status update still drops it.
    if (selfWorkspaceMemberId && data.chatMessage?.sender?.id === selfWorkspaceMemberId) {
      client.refetchQueries({ include: [QUERY_LIST_SCHEDULED_CHAT_MESSAGES] });
    }

    // A new message in the room, received or forwarded (your own forward arrives only here too).
    // A forward's files live on its source, so they are listed from there; a removed source has none.
    const roomId = data.chatMessage?.room?.id;
    const forwardedChat = data.chatMessage?.forwardedSource?.memberChat;
    cachePrependRoomAttachments(roomId, [
      ...(data.chatMessage?.chatAttachments ?? []),
      ...(forwardedChat?.isDeleted ? [] : (forwardedChat?.chatAttachments ?? [])),
    ]);
    // A channel forward's MMS arrive as its own chatAttachments (copied to S3) and are prepended above;
    // refetch only when it has media but no copies came with it.
    if (
      (data.chatMessage?.forwardedSource?.channelConversation?.numMedia ?? 0) > 0 &&
      !data.chatMessage?.chatAttachments?.length
    ) {
      cacheRefetchRoomAttachments(roomId);
    }

    cache.updateQuery(
      {
        query: CURSOR_MEMBER_MESSAGES,
        variables: {
          roomId: data.chatMessage?.room?.id,
        },
      },
      (cachedData) => {
        const newMessage = {
          ...data?.chatMessage,
          isPinned: false,
          messageConversation: {
            conversationStatus: null,
            effectiveEndedAt: null,
            effectiveStartedAt: null,
            id: null,
          },
        };

        return {
          ...cachedData,
          getChatMessagesV2: {
            ...cachedData?.getChatMessagesV2,
            data: [newMessage, ...(cachedData?.getChatMessagesV2?.data || [])],
          },
        };
      }
    );
  }
}
